import { clone, isEmpty, mergeDeepRight, omit } from 'ramda';
import { useState } from 'react';
import {
  useUpdateAccountMutation,
  useUpdateFlowSettingsMutation,
  type AccountSettings,
  type GetAccountQuery,
} from '~/graphql/types';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import type { BrandSettingsState } from '../../state';
import useAddToast from '~/hooks/useAddToast';
import formatToastMessage from '~/util/formatToastMessage';
import Catalog from '~/Catalog';
import useSessionHydration from '~/hooks/useSessionHydration';

/** Reset the initial recoil state to the latest state */
type ResetFn = () => void;

const text = {
  mutationError: Catalog.genericUnknownErrorMessage,
  success: Catalog.genericSuccessMessage,
};

/**
 * Handles update mutations within the Brand settings page.
 *
 * @returns onUpdate function that accepts an additional onReset callback to reset the initial state after a successful update etc.
 * @returns loading state of the mutation calls. It is loading until all mutations are finished.
 */
const useUpdateMutations = ({
  changedFields,
  prevData,
  flowsAllowed,
}: {
  changedFields: BrandSettingsState;
  prevData: GetAccountQuery['getAccount'];
  flowsAllowed: boolean;
}): {
  onUpdate: (params: { onReset: ResetFn }) => Promise<void>;

  /** Loading is true until all mutations are completed. */
  loading: boolean;
} => {
  const addToast = useAddToast();
  const [, refetchSession] = useSessionHydration();

  const { id: accountId } = useCurrentAccount();
  const [loading, setLoading] = useState(false);

  const [updateFlowSettings] = useUpdateFlowSettingsMutation();
  const [updateAccount] = useUpdateAccountMutation();

  const onUpdate = async ({ onReset }: { onReset: ResetFn }) => {
    if (!prevData?.AccountSettings) return;

    let hasError = false;
    setLoading(true);

    if (flowsAllowed === true && changedFields.unsubscribeLinkText) {
      const { data, errors } = await updateFlowSettings({
        variables: {
          accountId,
          update: { unsubscribeLinkText: changedFields.unsubscribeLinkText },
        },
      });

      if (!data || (errors && errors.length > 0)) {
        hasError = true;
      }
    }

    const updatedAccountFields: Omit<
      BrandSettingsState,
      'unsubscribeLinkText'
    > = clone(omit(['unsubscribeLinkText'], changedFields));

    if (!isEmpty(updatedAccountFields)) {
      const prevAccountSettings = convertAccountSettingsToInput(
        prevData.AccountSettings,
      );
      const mergedFields = mergeDeepRight(
        prevAccountSettings,
        updatedAccountFields,
      );

      const { data, errors } = await updateAccount({
        variables: {
          accountId,
          update: { settings: mergedFields },
        },
      });

      if (!data || (errors && errors.length > 0)) {
        hasError = true;
      }
    }

    setLoading(false);

    if (hasError) {
      return addToast([formatToastMessage(text.mutationError, 'danger')]);
    }

    onReset();
    await refetchSession();
    addToast([formatToastMessage(text.success, 'success')]);
  };

  return { onUpdate, loading };
};

const convertAccountSettingsToInput = (
  accountSettings: AccountSettings,
): Partial<BrandSettingsState> => {
  const result: Partial<BrandSettingsState> = {
    url: accountSettings.url,
  };

  if (accountSettings.colors) {
    result.colors = {
      primary: {
        background: accountSettings.colors.primary.background,
        color: accountSettings.colors.primary.color,
      },
      secondary: {
        background: accountSettings.colors.secondary.background,
        color: accountSettings.colors.secondary.color,
      },
    };
  }

  if (accountSettings.style) {
    result.style = {
      fontFamily: accountSettings.style.fontFamily,
    };
  }

  const imageFields = [
    { key: 'logo', value: accountSettings.logo },
    { key: 'teamImage', value: accountSettings.teamImage },
    { key: 'backgroundImage', value: accountSettings.backgroundImage },
  ];

  imageFields.forEach(({ key, value }) => {
    if (value) {
      result[key] = { s3key: value.s3key };
    }
  });

  return result;
};

export default useUpdateMutations;
